import { useContext, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import i18next from 'i18next';

import { useDeleteOrderMutation } from 'redux/api/ordersApi';
import { useAppDispatch } from 'redux/hooks';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { BucketContext } from 'contexts';
import { OrderContext } from 'contexts/OrderContext';

import { downloadOrderExport } from 'helpers/downloadOrderExport';
import { downloadOrderPdf } from 'helpers/downloadOrderPdf';
import { getOrderAvailablePriceTypes } from 'helpers/pricing';

import { routes } from 'data/routes';

import { Button } from 'components/Button';
import { DuplicateOrderModal } from 'components/DuplicateOrderModal';
import { IconName } from 'components/Icon';
import { InfoModal } from 'components/InfoModal';
import { PriceTypeOption } from 'components/PriceTypeModal';

type ButtonData = {
  id: number;
  onClick: () => void;
  label: string;
  icon?: IconName;
  isHidden?: boolean;
  isLoading?: boolean;
  motive?: 'actionSecondary' | 'cancelPrimary';
};

const useActions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    sendOrder,
    canSendOrder,
    isSend,
    order,
    customerInfo,
    customerDetails,
    orderHeader,
    isSendBtnLoading,
    isChartsOpen,
    toggleCharts,
  } = useContext(OrderContext);
  const { getUrl } = useContext(BucketContext);
  const { language } = i18next;

  const [deleteOrder] = useDeleteOrderMutation();
  const [isLoadingPDF, isLoadingPDFSet] = useState<boolean>();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSendModalVisible, setIsSendModalVisible] = useState<boolean>(false);
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState<boolean>(false);
  // Same list as the order page's price selector; undefined until the order loads.
  const availablePriceTypes = useMemo(
    () => (order?.items ? getOrderAvailablePriceTypes(order.items) : undefined),
    [order?.items]
  );

  const [priceTypeModalAction, setPriceTypeModalAction] = useState<'pdf' | 'export' | null>(
    null
  );

  const handlePriceTypeConfirm = async (selectedType: PriceTypeOption) => {
    setPriceTypeModalAction(null);

    const priceType = selectedType === 'NO_PRICE' ? undefined : selectedType;

    if (priceTypeModalAction === 'pdf') {
      if (!orderHeader) return;
      isLoadingPDFSet(true);
      try {
        await downloadOrderPdf({
          orderId: orderHeader.orderId,
          customerInfo: customerInfo || undefined,
          customerId: customerInfo?.id ? Number(customerInfo.id) : undefined,
          customerDetails,
          priceType,
        });
      } catch {
        toast.error(t('order.downloadFailed'));
      } finally {
        isLoadingPDFSet(false);
      }
    } else if (priceTypeModalAction === 'export') {
      if (!order || !id) return;
      setIsExporting(true);
      try {
        await downloadOrderExport({
          orderId: id,
          lang: language,
          orderNumber: orderHeader?.orderNumber ?? id,
          getUrl,
          priceType,
        });
      } catch {
        toast.error(t('order.exportFailed'));
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleDeleteOrder = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteOrder(id).unwrap();
      localStorage.setItem('orderNumber', '');
      localStorage.setItem('orderId', '');
      dispatch(setOrderNumber(''));
      dispatch(setOrderId(''));
      toast.success(t('order.deletedSuccessfully'));
      setIsDeleteModalVisible(false);
      navigate(routes.viewOrders);
    } catch (error) {
      console.error(error);
      toast.error(t('order.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const openSendModal = () => {
    // Validate first so the user is not asked to confirm an order that cannot be sent.
    if (canSendOrder()) setIsSendModalVisible(true);
  };

  const handleSendOrder = async () => {
    const isSent = await sendOrder();
    if (isSent) {
      // A sent order is read-only, so go back to the order list.
      navigate(routes.viewOrders);
      return;
    }
    setIsSendModalVisible(false);
  };

  const buttonsData: ButtonData[] = [
    {
      id: 0,
      label: t('order.deleteConfirmTitle'),
      icon: 'FiX',
      motive: 'cancelPrimary',
      onClick: () => setIsDeleteModalVisible(true),
      isHidden: isSend,
      isLoading: isDeleting,
    },
    {
      id: 1,
      label: t('common.copy'),
      icon: 'FiCopy',
      onClick: () => setIsDuplicateModalVisible(true),
    },
    {
      id: 2,
      label: isChartsOpen ? t('orderCharts.hide') : t('orderCharts.show'),
      icon: 'FiBarChart2',
      onClick: toggleCharts,
      // Available in every status (also after send); nothing to chart on an empty order.
      isHidden: !order?.items?.length,
    },
    {
      id: 3,
      label: t('product.downloadPdf'),
      isLoading: isLoadingPDF,
      onClick: () => setPriceTypeModalAction('pdf'),
    },
    {
      id: 4,
      label: t('selection.export'),
      isLoading: isExporting,
      onClick: () => setPriceTypeModalAction('export'),
    },
    {
      id: 5,
      label: t('order.sendOrder'),
      onClick: openSendModal,
      icon: 'FiChevronRight',
      isHidden: isSend,
      isLoading: isSendBtnLoading,
    },
  ];

  const buttonsDataIntoComponent = buttonsData?.map((item) =>
    !item.isHidden ? (
      <Button
        key={item.id}
        onClick={item.onClick}
        iconLeft={item.icon}
        // One icon size for every action button (rem).
        icoarea="1.6"
        motive={item.motive || 'actionSecondary'}
        fontFamily="LFT Zeno"
        scale="xs"
        isLoading={item.isLoading || false}
      >
        {item.label}
      </Button>
    ) : undefined
  );

  const deleteModal = isDeleteModalVisible ? (
    <InfoModal
      title={t('order.deleteConfirmTitle')}
      info={t('order.deleteConfirm')}
      onClose={() => setIsDeleteModalVisible(false)}
      onAction={handleDeleteOrder}
      actionText={t('common.delete')}
      isLoading={isDeleting}
    />
  ) : null;

  const sendModal = isSendModalVisible ? (
    <InfoModal
      title={t('order.sendOrder')}
      info={t('order.sendConfirm')}
      onClose={() => {
        if (!isSendBtnLoading) setIsSendModalVisible(false);
      }}
      onAction={handleSendOrder}
      actionText={t('common.send')}
      isLoading={isSendBtnLoading}
    />
  ) : null;

  const duplicateModal =
    isDuplicateModalVisible && id && orderHeader ? (
      <DuplicateOrderModal
        orderId={id}
        customerCode={orderHeader.customerCode}
        customerName={customerInfo?.companyName}
        onClose={() => setIsDuplicateModalVisible(false)}
      />
    ) : null;

  return {
    buttonsDataIntoComponent,
    deleteModal,
    sendModal,
    duplicateModal,
    priceTypeModalAction,
    availablePriceTypes,
    handlePriceTypeConfirm,
    closePriceTypeModal: () => setPriceTypeModalAction(null),
  };
};

export { useActions };
